/** Record priority, as the Teltonika AVL record carries it. */
export enum RecordPriority {
    Low   = 0,
    High  = 1,
    Panic = 2,
}

/** The two codecs an FMC003 speaks (src/libs/go/Teltonika). */
export enum CodecID {
    Codec8         = 8,
    Codec8Extended = 142,
}

/*
 * Which half of the world a parameter describes.
 *
 * The AVL name table prefixes everything that came off the vehicle's own bus with
 * "OBD " or "OEM " (Teltonika/Parameters.go). That prefix is the whole point of this
 * capture — it separates what the tracker knows about itself (voltage, GSM signal,
 * ignition) from what the car told it — so the grouping is derived from the name
 * rather than from a list of ids, which would go stale the moment the table grows.
 */
export enum ParameterGroup {
    OBD    = 'obd',
    OEM    = 'oem',
    Device = 'device',
}

/*
 * The body shapes a vehicle can be given.
 *
 * The same seven the API validates against — an empty string is also allowed there
 * and means "nobody has said yet", which is why this is not exhaustive over the
 * field.
 */
export enum VehicleBody {
    Sedan     = 'sedan',
    Estate    = 'estate',
    Hatchback = 'hatchback',
    SUV       = 'suv',
    Van       = 'van',
    Pickup    = 'pickup',
    Truck     = 'truck',
}

/*
 * What drives the vehicle, as a person says it.
 *
 * The API also accepts an empty string, which means "work it out from what the vehicle
 * reports". That inference is right for a tracker that has been driving and wrong for
 * one that has never sent a packet — which is the whole reason a person can say.
 */
export enum Drivetrain {
    Combustion = 'ice',
    Electric   = 'ev',
    Hybrid     = 'hybrid',
}

/** How the instruments are drawn. Remembered per browser under `gaugeStyle`. */
export enum GaugeStyle {
    Analogue = 'analogue',
    Digital  = 'digital',
}

/** The periods the track view offers, in hours. */
export enum TrackPeriod {
    LastHour = '1',
    LastDay  = '24',
    LastWeek = '168',
}

/** Where the name of a trip's end came from (fleet-api DataStructures/Trip.go). */
export enum PlaceSource {
    Named = 'place',
    OSM   = 'osm',
}

/** Which way a trip ran along its route; a round trip has neither. */
export enum TripDirection {
    Out  = 'out',
    Back = 'back',
}

/*
 * How much faster than real time a trip is replayed.
 *
 * ×30 is the default because it is the one that makes an hour's drive a two-minute
 * film — long enough to follow a road, short enough to watch to the end.
 */
export enum ReplaySpeed {
    Slow   = '10',
    Normal = '30',
    Fast   = '60',
    Rapid  = '120',
}

/*
 * What the tracker was doing when it sent a record — AVL 200, "Sleep Mode".
 *
 * The values are the wiki's own (FMC003 Teltonika Data Sending Parameters ID). Only
 * None means the box is fully awake; in every other mode it has powered something
 * down, and from Deep upwards that includes the GNSS receiver — which is why a record
 * with no fix is not necessarily a record that failed to get one.
 */
export enum SleepMode {
    None   = 0,
    GPS    = 1,
    Deep   = 2,
    Online = 3,
    Ultra  = 4,
}

/** How long a vehicle stands before a trip counts as over, in minutes. */
export enum TripStop {
    Short   = '5',
    Medium  = '15',
    Default = '30',
    Long    = '60',
}

/*
 * Which way the drives under the map are presented.
 *
 * Trips is the logbook's answer — one row per drive, the odometer beside it. Day is the
 * driver's: the same data read as one run of drives and standstills from midnight to
 * midnight, which is how somebody who was in the car remembers it. Remembered per
 * browser under `deviceTripsView`.
 */
export enum TripsView {
    Trips = 'trips',
    Day   = 'day',
}
